// @ts-strict-ignore
import { faCheckCircle } from "@fortawesome/pro-solid-svg-icons";
import { DropdownMenu } from "@fullscript/aviary-web";
import type { ReactNode, SyntheticEvent } from "react";

import { Checkbox, DropdownItem, Typography, Button, Arrange } from "@aviary";
import { FontAwesomeIcon } from "@shared/react-fontawesome/react-fontawesome";
import type { Variant } from "@shared/types/graphqlGenerated";

import { useIsBackorderNotifiableQuery } from "./isBackorderNotifiable.query";
import { useIsBackorderNotificationSubscribedQuery } from "./isBackorderNotificationSubscribed.query";

type VariantSplinter = Pick<Variant, "id">;

interface Props {
  turnOffReminder: () => void;
  turnOffReminderLoading: boolean;
  turnOnReminder: () => void;
  turnOnReminderLoading: boolean;
  variant: VariantSplinter;
  restockReminderOnText?: string;
  isButton?: boolean;
  useAviaryWebMenuItem?: boolean;
  children: ReactNode;
}

const RestockReminder = ({
  children,
  turnOffReminder,
  turnOffReminderLoading,
  turnOnReminder,
  turnOnReminderLoading,
  variant,
  restockReminderOnText,
  useAviaryWebMenuItem,
  isButton,
}: Props) => {
  const { data: queryIsReminderEnabledData, loading: isBackorderNotifiableLoading } =
    useIsBackorderNotifiableQuery({
      variables: { variantId: variant.id },
    });

  const { data: queryIsReminderTurnedOnData, loading: isReminderTurnedOnLoading } =
    useIsBackorderNotificationSubscribedQuery({
      variables: { variantId: variant.id },
    });

  const isChecked =
    !!queryIsReminderTurnedOnData?.viewer?.variant?.isBackorderNotificationSubscribed;

  const isDisabled =
    isReminderTurnedOnLoading ||
    isBackorderNotifiableLoading ||
    !queryIsReminderEnabledData?.viewer?.variant?.isBackorderNotifiable ||
    turnOffReminderLoading ||
    turnOnReminderLoading;
  const onSelect = (e: SyntheticEvent) => {
    e.preventDefault();
    toggleCheck();
  };

  const toggleCheck = () => (isChecked ? turnOffReminder() : turnOnReminder());

  if (isButton) {
    if (isChecked) {
      return (
        <Button isColor="system" disabled={isDisabled} onClick={onSelect} isFullWidth>
          <Arrange isWrapped={false} alignment="center" spacing="xxsmall">
            <FontAwesomeIcon icon={faCheckCircle} />

            <Typography>{restockReminderOnText}</Typography>
          </Arrange>
        </Button>
      );
    }

    return (
      <Button isColor="system" disabled={isDisabled} onClick={onSelect} isFullWidth>
        {children}
      </Button>
    );
  }

  if (useAviaryWebMenuItem) {
    return (
      <DropdownMenu.CheckboxItem
        key={variant.id}
        checked={isChecked}
        onCheckedChange={toggleCheck}
        {...(isDisabled && { disabled: true })}
      >
        {children as string}
      </DropdownMenu.CheckboxItem>
    );
  }

  return (
    <DropdownItem closeOnItemSelect={false} disabled={isDisabled} onSelect={onSelect}>
      <Checkbox checked={isChecked} disabled={isDisabled} readOnly>
        {children}
      </Checkbox>
    </DropdownItem>
  );
};

export { RestockReminder };
